import { useNavigate } from 'react-router-dom';
import Section, { Reveal } from '../components/Section';
import { IconCheck } from '../components/Icon';
import { useEffect, useState } from 'react';
import { API } from '../api';
import PromoBanner from '../components/PromoBanner';

type Plan = { id: string; name: string; annual: number; highlight?: boolean; features: string[]; cta: string };

// Annual-only pricing (monthly plans removed 2026-08-17 — matches backend).
const PLANS: Plan[] = [
  { id: 'free',     name: 'Free',     annual: 0,     features: ['Full REALITY tunnel', 'Standard server pool', 'Basic anti-DPI', 'Community support'], cta: 'Get started' },
  { id: 'gold',     name: 'Gold',     annual: 20.99, highlight: true, features: ['50 GB / month', 'All Gold servers', 'Priority routing', 'Live chat support', 'Coupon eligibility'], cta: 'Upgrade to Gold' },
  { id: 'platinum', name: 'Platinum', annual: 31.99, features: ['Unlimited data', 'Every server, top tier', 'First access to new nodes', 'Priority support (2h)', 'Manual server picker'], cta: 'Go Platinum' },
];

export default function Pricing() {
  const nav = useNavigate();
  const [promo, setPromo] = useState<any>(null);
  useEffect(() => { API.activePromo().then(setPromo); }, []);

  const isGlobalDiscount = promo && promo.reward_type === 'discount';
  const discountPct = isGlobalDiscount ? Number(promo.reward_value) || 0 : 0;

  const handleCta = (plan: Plan) => {
    if (plan.id === 'free') { nav('/download'); return; }
    // Require login/registered before checkout — mirrors Account Portal guard
    if (!API.loggedIn()) { nav(`/account?next=${encodeURIComponent(`/payments?plan=${plan.id}_annual`)}`); return; }
    const coupon = isGlobalDiscount ? `&coupon=${encodeURIComponent(promo.code)}` : '';
    nav(`/payments?plan=${plan.id}_annual${coupon}`);
  };

  return (
    <>
      {isGlobalDiscount && (
        <Section className="!pb-0 !pt-24 sm:!pt-28">
          <PromoBanner promo={promo} />
        </Section>
      )}
      <Section chip="Pricing" title="Simple, honest, no surprises." subtitle="Free forever tier. Annual plans only — no auto-renew traps, no monthly nickel-and-dime.">
        <div className="grid gap-5 md:grid-cols-3">
          {PLANS.map((p, i) => {
            const hasDiscount = isGlobalDiscount && p.annual > 0;
            const discounted = hasDiscount ? +(p.annual * (1 - discountPct / 100)).toFixed(2) : p.annual;
            return (
            <Reveal key={p.id} delay={i * 0.07}>
              <div className={`h-full p-8 flex flex-col ${p.highlight ? 'glass-2 ring-1 ring-[color:var(--color-brand)]/50' : 'glass'}`}>
                <div className="flex items-center justify-between">
                  <div className="chip">{p.name}</div>
                  {p.highlight && <div className="chip chip-ok">Most popular</div>}
                </div>
                <div className="mt-6 flex items-end gap-2">
                  {hasDiscount ? (
                    <>
                      <span className="h-title text-5xl font-extrabold">${discounted}</span>
                      <span className="text-[color:var(--color-ink-faint)] mb-2 text-sm line-through">${p.annual}</span>
                      <span className="text-[color:var(--color-ink-faint)] mb-2 text-sm">/yr</span>
                      <span className="ml-1 mb-2 text-xs font-bold text-[color:var(--color-brand-glow)]">−{discountPct}%</span>
                    </>
                  ) : (
                    <>
                      <span className="h-title text-5xl font-extrabold">${p.annual}</span>
                      <span className="text-[color:var(--color-ink-faint)] mb-2 text-sm">/yr</span>
                    </>
                  )}
                </div>
                {hasDiscount && p.annual > 0 && <div className="text-xs text-[color:var(--color-brand-glow)] mt-1">Global coupon {promo.code} auto-applied at checkout</div>}
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-[color:var(--color-ink-dim)]">
                      <span className="text-[color:var(--color-ok)] flex-none mt-0.5"><IconCheck width={16} height={16} /></span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleCta(p)} className={`mt-8 btn justify-center w-full ${p.highlight ? 'btn-primary' : 'btn-ghost'}`}>{p.cta}</button>
              </div>
            </Reveal>
          );})}
        </div>
      </Section>

      <Section chip="Fine print" title="Full transparency.">
        <div className="grid gap-5 md:grid-cols-2">
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Refunds</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Within 7 days of purchase if less than 5% of the plan's monthly data has been consumed. Email <a className="text-[color:var(--color-brand-glow)] underline" href="mailto:billing@tnidu.com">billing@tnidu.com</a>.</p>
          </div>
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Coupons</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Free-days coupons grant temporary Gold access without payment. Discount coupons stack with annual pricing. Enter in-app during upgrade.</p>
          </div>
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Auto-renew</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Off by default. You get a system notification 3 days before expiry; renew only if you want to.</p>
          </div>
          <div className="glass p-6">
            <h3 className="font-[family-name:var(--font-display)] font-bold text-lg mb-2">Payment</h3>
            <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">Direct card entry (Stripe · PCI-DSS SAQ A) and Alipay. Vpnee never sees your card details.</p>
          </div>
        </div>
      </Section>
    </>
  );
}
